import React, { useState } from "react";
import { ExclamationTriangleIcon, ArrowsPointingOutIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { STATUS_COLORS, SENSOR_INFO, RISK_INFO, METRIC_INFO, TOOLTIPS } from "../utils/constants";
import { SensorDetailModal } from "./SensorDetailModal";
import { InfoTooltip } from "./InfoTooltip";

/**
 * Parse simple markdown (bold **text**) into React elements.
 */
function parseSimpleMarkdown(text) {
  if (!text) return text;

  // Split by **text** pattern and convert to React elements
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove ** and wrap in strong
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

/**
 * RiskGauge component displays a visual risk indicator for an asset.
 */
export function RiskGauge({ riskData, assetId, aiEnabled = true, groundTruthRul = null }) {
  if (!aiEnabled) {
    return (
      <div className="risk-gauge ai-disabled">
        <h3>Risk Assessment</h3>
        <div className="gauge-placeholder">
          <span className="placeholder-icon">—</span>
          <p>Inference Disabled</p>
          <span className="hint">Enable in settings to see predictions</span>
        </div>
      </div>
    );
  }

  if (!riskData) {
    return (
      <div className="risk-gauge">
        <h3>Risk Assessment</h3>
        <div className="gauge-placeholder">
          <span className="placeholder-icon">...</span>
          <p>Waiting for prediction...</p>
        </div>
      </div>
    );
  }

  const {
    risk_score, confidence, top_features, alert, mock, explanation,
    ml_risk_score, ml_predicted_rul, used_ml_model
  } = riskData;
  const percentage = (risk_score * 100).toFixed(1);
  const riskLevel = getRiskLevel(risk_score);
  const riskColor = getRiskColor(risk_score);

  // ML model predictions
  const hasMLPrediction = used_ml_model && ml_predicted_rul != null;
  const mlPercentage = ml_risk_score != null ? (ml_risk_score * 100).toFixed(1) : null;

  // Determine risk state class
  const riskStateClass = alert ? "alert-active" :
                         risk_score >= 0.3 ? "warning-active" : "";

  return (
    <div className={`risk-gauge ${riskStateClass}`}>
      <div className="gauge-header">
        <h3>Risk Assessment</h3>
        {mock && <span className="mock-badge">MOCK</span>}
      </div>

      <div className="gauge-visual">
        <svg viewBox="0 0 200 120" className="gauge-svg">
          {/* Background arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="#374151"
            strokeWidth="20"
            strokeLinecap="round"
          />
          {/* Colored arc based on risk */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke={riskColor}
            strokeWidth="20"
            strokeLinecap="round"
            strokeDasharray={`${risk_score * 251} 251`}
            style={{ transition: "stroke-dasharray 0.3s ease" }}
          />
          {/* Center text */}
          <text
            x="100"
            y="85"
            textAnchor="middle"
            fill={riskColor}
            fontSize="36"
            fontWeight="bold"
          >
            {percentage}%
          </text>
          <text
            x="100"
            y="110"
            textAnchor="middle"
            fill="#9ca3af"
            fontSize="14"
          >
            {riskLevel}
          </text>
        </svg>
      </div>

      {alert && (
        <div className="alert-banner">
          <ExclamationTriangleIcon className="h-5 w-5" />
          <span>FAILURE RISK DETECTED</span>
        </div>
      )}

      <div className="gauge-details">
        <div className="detail-row">
          <span className="label">
            Confidence
            <InfoTooltip text={TOOLTIPS.confidence} size={12} />
          </span>
          <span className="value">{(confidence * 100).toFixed(0)}%</span>
        </div>
        <div className="detail-row">
          <span className="label">
            Top Indicators
            <InfoTooltip text={TOOLTIPS.topIndicators} size={12} />
          </span>
          <div className="feature-tags">
            {top_features?.slice(0, 3).map((feature, idx) => (
              <span key={idx} className="feature-tag" title={getFeatureExplanation(feature)}>
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ML Model Predictions */}
      {hasMLPrediction && (
        <div className="ml-predictions">
          <div className="ml-header">
            <span>ML Prediction</span>
          </div>
          <div className="ml-content">
            <div className="ml-item rul-prediction">
              <div className="ml-values">
                <span className="ml-label">Est. Remaining Life</span>
                <span className="ml-value">~{Math.round(ml_predicted_rul)} cycles</span>
              </div>
            </div>
            {mlPercentage && (
              <div className="ml-item ml-risk">
                <span className="ml-label">ML Risk</span>
                <span className="ml-value" style={{ color: getRiskColor(ml_risk_score) }}>
                  {mlPercentage}%
                </span>
              </div>
            )}
          </div>
          {/* Ground Truth Comparison (test data only) */}
          {groundTruthRul != null && (
            <div className="ground-truth-comparison">
              <div className="gt-header">
                <span className="gt-label">Ground Truth RUL</span>
                <span className="gt-value">{groundTruthRul} cycles</span>
              </div>
              <div className="gt-error">
                <span className="gt-label">Prediction Error</span>
                <span className={`gt-value ${Math.abs(ml_predicted_rul - groundTruthRul) <= 15 ? 'gt-accurate' : 'gt-off'}`}>
                  {ml_predicted_rul > groundTruthRul ? '+' : ''}{Math.round(ml_predicted_rul - groundTruthRul)} cycles
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ground Truth RUL (when no ML prediction) */}
      {!hasMLPrediction && groundTruthRul != null && (
        <div className="ground-truth-only">
          <div className="gt-header">
            <span>Ground Truth (Test Data)</span>
          </div>
          <div className="gt-content">
            <span className="gt-label">Actual RUL</span>
            <span className="gt-value">{groundTruthRul} cycles</span>
          </div>
        </div>
      )}

      {/* Risk explanation */}
      <div className={`risk-explanation ${explanation ? "ai-generated" : ""}`}>
        <div className="explanation-header">
          <span>What this means</span>
        </div>
        <p className="explanation-text">
          {parseSimpleMarkdown(explanation || `${RISK_INFO[riskLevel]?.meaning} - ${RISK_INFO[riskLevel]?.action}`)}
        </p>
      </div>
    </div>
  );
}

function getFeatureExplanation(feature) {
  // Parse feature like "S7_slope" or "S11_std"
  const match = feature.match(/S(\d+)_(\w+)/);
  if (!match) return feature;

  const sensorKey = `sensor_${match[1]}`;
  const metricKey = match[2];
  const sensorInfo = SENSOR_INFO[sensorKey];
  const metricInfo = METRIC_INFO[metricKey];

  if (!sensorInfo) return feature;

  return `${sensorInfo.name}: ${metricInfo?.interpretation || metricKey}`;
}

/**
 * Calculate sensor importance score for sorting
 */
function getSensorImportanceScore(sensor, features) {
  if (!features) return 0;

  const sensorInfo = SENSOR_INFO[sensor];
  const baseScore = sensorInfo?.importance === "critical" ? 100 :
                    sensorInfo?.importance === "high" ? 50 :
                    sensorInfo?.importance === "medium" ? 25 : 10;

  const slopeScore = Math.abs(features.slope || 0) * 10000;
  const stdScore = (features.std || 0) * 10;

  return baseScore + slopeScore + stdScore;
}

/**
 * FeaturePanel shows detailed feature values for an asset.
 * Shows top 6 sensors by current importance with option to expand and see all.
 */
export function FeaturePanel({ features, assetId }) {
  const [showModal, setShowModal] = useState(false);

  if (!features) {
    return (
      <div className="feature-panel">
        <h3>Feature Details</h3>
        <div className="panel-placeholder">
          <span className="placeholder-icon">...</span>
          <p>Waiting for data...</p>
          <span className="placeholder-hint">
            Features compute after 2+ cycles
          </span>
        </div>
      </div>
    );
  }

  const { sensor_features, window_start, window_end, window_size, window_max, window_ready } = features;
  const warmingUp = window_ready === false;
  const progress = window_max ? Math.round((window_size / window_max) * 100) : 100;

  // Dynamically sort sensors by importance (changes over time based on data)
  const allSensors = Object.keys(sensor_features || {});
  const sortedSensors = allSensors.sort((a, b) => {
    const scoreA = getSensorImportanceScore(a, sensor_features[a]);
    const scoreB = getSensorImportanceScore(b, sensor_features[b]);
    return scoreB - scoreA;
  });

  // Show top 6 sensors
  const displaySensors = sortedSensors.slice(0, 6);
  const totalSensors = allSensors.length;

  return (
    <div className={`feature-panel ${warmingUp ? "warming-up" : ""}`}>
      <div className="panel-header">
        <h3>
          Feature Details
          <span className="info-tooltip" title="Statistical features computed over a sliding window of sensor readings">
            <InformationCircleIcon className="h-3.5 w-3.5" />
          </span>
        </h3>
        <span className="window-info">
          Window: {window_start} - {window_end} ({window_size}{window_max ? `/${window_max}` : ""} cycles)
          {warmingUp && <span className="warming-badge">WARMING UP</span>}
        </span>
      </div>

      {warmingUp && (
        <div className="warming-progress">
          <div className="warming-bar">
            <div className="warming-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="warming-text">{progress}% - Early estimates (less reliable)</span>
        </div>
      )}

      {/* Metric legend */}
      <div className="metric-legend">
        <span className="legend-item" title={METRIC_INFO.mean.interpretation}>
          <strong>Mean</strong>: avg value
        </span>
        <span className="legend-item" title={METRIC_INFO.std.interpretation}>
          <strong>Std</strong>: variability
        </span>
        <span className="legend-item" title={METRIC_INFO.slope.interpretation}>
          <strong>Slope</strong>: trend/cycle
        </span>
      </div>

      <div className="feature-grid">
        {displaySensors.map((sensor) => {
          const values = sensor_features[sensor];
          const sensorInfo = SENSOR_INFO[sensor] || {};
          const sensorUnit = sensorInfo.unit || "";

          return (
            <div
              key={sensor}
              className={`feature-card importance-${sensorInfo.importance || "medium"}`}
              title={`${sensorInfo.description}\n\nSignificance: ${sensorInfo.significance}`}
            >
              <div className="sensor-name">
                <span className="sensor-id-badge">
                  {sensor.replace("sensor_", "S")}
                  {sensorInfo.importance === "critical" && (
                    <ExclamationTriangleIcon className="h-2.5 w-2.5 critical-icon" />
                  )}
                </span>
                <span className="sensor-fullname">{sensorInfo.name}</span>
              </div>
              <div className="feature-values">
                <div className="feature-item">
                  <span className="label">Mean</span>
                  <span className="value">
                    {values.mean?.toFixed(2)}
                    {sensorUnit && <span className="unit">{sensorUnit}</span>}
                  </span>
                </div>
                <div className="feature-item">
                  <span className="label">Std</span>
                  <span className="value">{values.std?.toFixed(3)}</span>
                </div>
                <div className={`feature-item ${getSlopeClass(values.slope)}`}>
                  <span className="label">Slope</span>
                  <span className="value">
                    {values.slope >= 0 ? "+" : ""}
                    {values.slope?.toFixed(4)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Expand button */}
      <button className="expand-sensors-btn" onClick={() => setShowModal(true)}>
        <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
        View all {totalSensors} sensors with details
      </button>

      {/* Modal for all sensors */}
      <SensorDetailModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        sensorFeatures={sensor_features}
        assetId={assetId}
      />
    </div>
  );
}

function getRiskLevel(score) {
  if (score >= 0.7) return "CRITICAL";
  if (score >= 0.5) return "ELEVATED";
  if (score >= 0.3) return "MODERATE";
  return "LOW";
}

function getRiskColor(score) {
  if (score >= 0.7) return STATUS_COLORS.ALERT;
  if (score >= 0.5) return STATUS_COLORS.WARNING;
  return STATUS_COLORS.OK;
}

function getSlopeClass(slope) {
  if (!slope) return "";
  if (slope < -0.05) return "slope-negative";
  if (slope > 0.05) return "slope-positive";
  return "";
}
